import './TargetingDropdowns.scss';

import React, { useCallback, useMemo } from 'react';

import Button from '../../common/Button';
import Dropdown from '../../common/Dropdown';
import type { TargetingTermValue } from '../../models';
import bem from '../../utils/bem';
import type { ListHelpers } from '../../utils/listHelpers';
import type { AdFormValues } from '../AdForm/adFormik';
import type { DropdownOption } from '../Dropdown';
import CustomMenuWithTooltip from '../Dropdown/CustomMenuWithTooltip';
import CustomOptionWithTooltip from '../Dropdown/CustomOptionWithTooltip';
import { commonCustomComponents } from '../Dropdown/Dropdown';
import { useFieldFast } from '../Form/hooks';
import type { TargetingScope } from '../Targeting';
import {
  COUNTRY_SCOPED_CATEGORY_LABELS,
  MISMATCHED_COUNTRIES_CATEGORY_TOOLTIP,
  MISMATCHED_COUNTRIES_CATEGORY_TOOLTIP_ID,
} from '../Targeting';
import { GeoDimension } from './constants';
import { useTargetingDropdownSelection } from './hooks';
import type { TargetingDropdownOption, UseTargetingDropdownsReturn } from './hooks/useTargetingDropdowns';
import useTargetingDropdowns from './hooks/useTargetingDropdowns';
import TargetingValuesDropdown from './TargetingValuesDropdown';
import { getEmptyTextMessage, getSupportedDimensionOptions, isTargetingActionDisabled } from './utils';

export type TargetingDropdownProps = {
  /**
   * The listHelpers prop is a set of functions to help keep the list store up to date.
   *
   * We use insertAt for adding new items to the list that is selected, and replaceAt will replace the record
   * at a specific index to update that record when using the toggles.
   * And removeAt for remove specific targeting from targetingTable
   */
  listHelpers: Pick<ListHelpers<TargetingTermValue>, 'insertAt' | 'replaceAt' | 'removeAt'>;
  /** A boolean flag to enable a select all option in the dropdown. */
  readonly?: boolean;
  /** An array of TargetingTermValues that represent what has been added to the Targeting Table. */
  targetings: TargetingTermValue[];
  /** Validation error message if any */
  validationError?: string;
  /**
   * Read-only configuration describing the entity (or entities) being targeted.
   * See `TargetingScope` for field-by-field documentation.
   */
  scope: TargetingScope;
};

const [block, element] = bem('targeting-dropdowns');

// Wires per-option tooltips through `react-select`. `CustomOptionWithTooltip`
// adds the `data-tip` / `data-for` wrapper that `react-tooltip` listens for,
// and `CustomMenuWithTooltip` mounts the single `ReactTooltip` instance the
// dimmed Category options anchor to. Without these the tooltip is silently
// dropped on the floor (the cursor still goes `not-allowed`, but no tooltip).
const categoryDropdownComponents = {
  ...commonCustomComponents,
  Option: CustomOptionWithTooltip,
  Menu: CustomMenuWithTooltip,
};

/**
 * A ReactJS component for managing targeting rules for Ads and LineItems.
 * @param listHelpers Object Contains functions to manage the list store, including insertAt, replaceAt, and removeAt.
 * @param readonly boolean Indicates if the dropdowns should be in read-only mode.
 * @param targetings Array Represents the current targeting rules added to the Targeting Table.
 * @param validationError string Validation error message, if any.
 * @param scope Read-only configuration object describing the entity / entities being targeted. See `TargetingScope`.
 * @constructor
 */
const TargetingDropdowns = ({
  listHelpers,
  readonly,
  targetings,
  validationError,
  scope,
}: TargetingDropdownProps): React.JSX.Element => {
  const {
    isAdEntity,
    lineItemCountryIds,
    countryIdsForFilteringValues,
    lineItemPublishers,
    adTypes,
    entitiesHaveMismatchedCountries,
  } = scope;

  const [, { value: newTargeting }, { setValue: setNewTargetingRaw }] = useFieldFast<AdFormValues['newTargeting']>(
    'newTargeting'
  );
  const setNewTargeting = useCallback(
    (val: AdFormValues['newTargeting']): void => {
      setNewTargetingRaw(val);
    },
    [setNewTargetingRaw]
  );
  const {
    categoryOptions,
    selectedCategory,
    setSelectedCategory,
    isLoadingCategories,
    dimensionsOptions,
    selectedDimension,
    setSelectedDimension,
    isLoadingDimensionsByCategory,
    errorGettingDimensionsByCategory,
    selectedValues,
    setSelectedValues,
    clearAll,
  }: UseTargetingDropdownsReturn = useTargetingDropdowns(newTargeting, setNewTargeting, scope);

  const {
    currentSelectedValues,
    handleInsert,
    handleSelectValues,
    handleClearAll,
    handleSelectAll,
    selectedDimensionValues,
    onClose,
    onClearFormStateAndSelectedValues,
  } = useTargetingDropdownSelection({
    listHelpers,
    targetings,
    selectedCategory,
    selectedDimension,
    selectedValues: selectedValues ?? [],
    clearAll,
    setSelectedValues,
  });

  const filteredDimensionOptions = useMemo(
    () =>
      getSupportedDimensionOptions({
        dropdownOptions: dimensionsOptions,
        isAdEntity,
        lineItemPublishers,
        adTypes,
      }),
    [adTypes, dimensionsOptions, isAdEntity, lineItemPublishers]
  );

  // When entities being multi-edited target different countries, dim category
  // options whose values are inherently scoped to a single country/market and
  // attach an explanatory tooltip. Every dimmed option uses the same tooltip
  // `id` so its `data-for` resolves to the single `ReactTooltip` instance
  // mounted by `CustomMenuWithTooltip` (see the `tooltip` prop passed to the
  // Category dropdown below).
  const decoratedCategoryOptions = useMemo(() => {
    if (!entitiesHaveMismatchedCountries) return categoryOptions;
    return categoryOptions.map((option) =>
      COUNTRY_SCOPED_CATEGORY_LABELS.includes(option.label)
        ? {
            ...option,
            isDisabled: true,
            tooltip: {
              id: MISMATCHED_COUNTRIES_CATEGORY_TOOLTIP_ID,
              message: MISMATCHED_COUNTRIES_CATEGORY_TOOLTIP,
            },
          }
        : option
    );
  }, [categoryOptions, entitiesHaveMismatchedCountries]);

  const isButtonDisabled = isTargetingActionDisabled({
    readonly,
    selectedDimension,
    previouslySelectedValues: selectedDimensionValues,
    currentSelectedValues,
    adTypes,
    lineItemPublishers,
    lineItemCountryIds,
  });

  // When selecting Country values, scope the available options to the line
  // item's countries (rather than the ad's already-selected country target).
  const valuesDropdownCountryIds =
    selectedDimension?.id === GeoDimension.Country ? lineItemCountryIds : countryIdsForFilteringValues;
  const valuesDropdownScope: TargetingScope = useMemo(
    () => ({ ...scope, countryIdsForFilteringValues: valuesDropdownCountryIds }),
    [scope, valuesDropdownCountryIds]
  );

  return (
    <div className={block()}>
      <div className={element('category-dimension-wrapper')}>
        <div className={element('category-dropdown')}>
          <Dropdown
            value={selectedCategory}
            label="Category"
            errMsg={validationError}
            options={decoratedCategoryOptions}
            isDisabled={isLoadingCategories || readonly}
            onChange={(value: DropdownOption | null): void =>
              setSelectedCategory({ id: value?.value, ...value } as TargetingDropdownOption)
            }
            components={categoryDropdownComponents}
            tooltip={{ id: MISMATCHED_COUNTRIES_CATEGORY_TOOLTIP_ID, effect: 'solid' }}
            dataTestId="targeting-category-dropdown"
          />
        </div>
        <div className={element('dimension-wrapper')}>
          <Dropdown
            value={selectedDimension}
            // While the data is referred to as a 'dimension' the UI labels it as a 'Type'
            label="Type"
            errMsg={validationError}
            options={filteredDimensionOptions || []}
            isLoading={isLoadingDimensionsByCategory}
            error={errorGettingDimensionsByCategory}
            isDisabled={!selectedCategory || isLoadingDimensionsByCategory || readonly}
            emptyDisplayText={getEmptyTextMessage(isLoadingDimensionsByCategory, selectedCategory)}
            onChange={(value: DropdownOption | null): void => {
              if (value === null) setSelectedDimension(null);
              setSelectedDimension({ id: value?.value || null, ...value } as TargetingDropdownOption);
            }}
            dataTestId="targeting-dimension-dropdown"
          />
        </div>
      </div>
      <div className={element('value-wrapper')}>
        <TargetingValuesDropdown
          handleSelect={handleSelectValues}
          selectedValues={selectedDimensionValues}
          handleSelectAll={handleSelectAll}
          handleClearAll={handleClearAll}
          selectedDimension={selectedDimension}
          onClose={onClose}
          errMsg={validationError}
          readonly={readonly}
          dataTestId="targeting-values-dropdown"
          scope={valuesDropdownScope}
        />
      </div>
      <div className={element('action-wrapper')}>
        <button
          disabled={readonly}
          className={element('clear')}
          title="Clear Selections"
          onClick={onClearFormStateAndSelectedValues}
        >
          Clear Selections
        </button>
        <div>
          <Button
            icon="IconSlash"
            size="medium"
            className={'exclude-btn'}
            onClick={(): void => handleInsert(false)}
            title="Exclude"
            disabled={isButtonDisabled}
          >
            Exclude {currentSelectedValues?.length > 0 ? currentSelectedValues?.length : ''}
          </Button>
          <Button
            icon="IconCheck"
            size="medium"
            className={'include-btn'}
            onClick={(): void => handleInsert(true)}
            title="Include"
            disabled={isButtonDisabled}
          >
            Include {currentSelectedValues?.length > 0 ? currentSelectedValues?.length : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TargetingDropdowns;
